import { BigNumber } from "@ethersproject/bignumber"
import { type Address } from "viem"
import { useBalance } from "wagmi"

import { truncateToMaxDecimals } from "@/common/helpers/number"
import { getLSTStakerConstants } from "@/governance/constants/lststaker"
import { chainIdToChainReference } from "@/web3/helpers/transformers"

type Args = { orgId?: string; accountAddress?: Address }

export function useLSTUserBalances({ orgId, accountAddress }: Args) {
  const { orgToken, lstToken, chainId } = getLSTStakerConstants(orgId)
  const chainReference = chainIdToChainReference(chainId)

  const {
    data: orgTokenBalanceData,
    isLoading: orgTokenBalanceLoading,
    refetch: refetchOrgTokenBalance,
  } = useBalance({
    address: accountAddress as Address,
    chainId: chainReference ?? undefined,
    token: orgToken.address as Address,
    enabled: Boolean(accountAddress),
  })

  const {
    data: lstTokenBalanceData,
    isLoading: lstTokenBalanceLoading,
    refetch: refetchLstTokenBalance,
  } = useBalance({
    address: accountAddress as Address,
    chainId: chainReference ?? undefined,
    token: lstToken.address as Address,
    enabled: Boolean(accountAddress),
  })

  const update = () => {
    refetchLstTokenBalance()
    refetchOrgTokenBalance()
  }

  const orgTokenBalance = {
    value: BigNumber.from(orgTokenBalanceData?.value ?? 0),
    loading: orgTokenBalanceLoading,
    formatted: truncateToMaxDecimals(
      orgTokenBalanceData?.formatted ?? "0",
      2,
      false,
    ),
  }

  const lstTokenBalance = {
    value: BigNumber.from(lstTokenBalanceData?.value ?? 0),
    loading: lstTokenBalanceLoading,
    formatted: truncateToMaxDecimals(
      lstTokenBalanceData?.formatted ?? "0",
      2,
      false,
    ),
  }

  return {
    orgTokenBalance,
    lstTokenBalance,
    update,
  }
}
